/**
 * Shared TanStack Query client.
 *
 * Defaults are tuned for internal apps that change infrequently:
 *   - staleTime 1 min so navigating between pages doesn't refetch constantly
 *   - no auto-refetch on window focus (more startling than helpful)
 *   - no retries on 401/403 — let ProtectedRoute redirect instead
 *
 * **`gcTime` is deliberately long.** It was 5 minutes, which is shorter than
 * the interval on which the data actually changes: every board is served from a
 * backend snapshot its poller rebuilds on a 5 min / 15 min / 6 h tier, so a
 * cached payload is still the current answer long after five minutes. The old
 * value meant leaving a board and coming back six minutes later dropped the
 * cache and showed a full skeleton to fetch bytes that had not changed —
 * loading screens as a pure artefact of the cache window.
 *
 * With a two-hour window, a revisited board paints from cache immediately and
 * `staleTime` triggers a quiet background refetch, which is what the dashboard
 * design rules mean by "refetch holds the frame". Cost is memory only: these
 * payloads are aggregates, tens of KB each.
 *
 * **Surviving a refresh** is a separate concern, handled in `./persist.ts`: an
 * in-memory cache dies with the page, so F5 reloaded every board from nothing
 * however long `gcTime` was. That module mirrors the cache into `sessionStorage`
 * — scoped to the tab and discarded when it closes, never `localStorage`, since
 * boards carry infrastructure data that has no business outliving the session on
 * someone's laptop — and it writes only an allow-list of aggregate queries, so
 * the payloads that name a person (the licence drill-down, the cloud change
 * feed) never reach storage at all. `maxAge` there tracks the `gcTime` below:
 * rehydration should not revive what this cache would itself have dropped.
 */

import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 2 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | undefined)?.status;
        if (status === 401 || status === 403) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
